"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useAuth } from "@workspace/ui/hooks/useAuth";
import { Card } from "@workspace/ui/components/card";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import { apiClient } from "@/lib/api-client";

interface ChangeRow {
  id: number;
  docId: string;
  docTitle: string | null;
  docType: string | null;
  field: string;
  kind: string;
  prev: unknown;
  next: unknown;
  actor: string | null;
  source: string;
  createdAt: number;
  rendered: string;
}

const PAGE_SIZE = 50;

export default function ChangesPage() {
  const { authenticated, loading } = useAuth({ requireAuth: true });
  const [changes, setChanges] = useState<ChangeRow[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authenticated) return;
    const es = new EventSource("/api/changes/stream", {
      withCredentials: true,
    });
    es.addEventListener("initial", (ev) => {
      try {
        const d = JSON.parse((ev as MessageEvent).data) as {
          changes: ChangeRow[];
        };
        setChanges(d.changes);
        if (d.changes.length < PAGE_SIZE) setDone(true);
        setError(null);
      } catch (e) {
        setError(String(e));
      }
    });
    es.addEventListener("change", (ev) => {
      try {
        const c = JSON.parse((ev as MessageEvent).data) as ChangeRow;
        setChanges((prev) => {
          if (prev.some((p) => p.id === c.id)) return prev;
          return [c, ...prev];
        });
      } catch {
        // ignore malformed event
      }
    });
    es.addEventListener("error", () => {
      setError((prev) => prev ?? "Connection lost — retrying…");
    });
    return () => {
      es.close();
    };
  }, [authenticated]);

  const loadMore = async () => {
    if (loadingMore || done || changes.length === 0) return;
    setLoadingMore(true);
    try {
      const oldest = changes[changes.length - 1].createdAt;
      const { data: d } = await apiClient.get<{ changes: ChangeRow[] }>(
        "/api/changes",
        { params: { limit: PAGE_SIZE, before: oldest } },
      );
      // De-dupe by id
      const ids = new Set(changes.map((c) => c.id));
      const fresh = d.changes.filter((c) => !ids.has(c.id));
      setChanges((prev) => [...prev, ...fresh]);
      if (fresh.length < PAGE_SIZE) setDone(true);
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading || !authenticated) {
    return (
      <div className="container max-w-4xl py-8">
        <div className="animate-pulse space-y-2">
          <div className="h-8 w-48 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container max-w-4xl py-8">
        <p className="text-destructive">Failed to load: {error}</p>
      </div>
    );
  }

  // Group by day
  const groups = new Map<string, ChangeRow[]>();
  for (const c of changes) {
    const day = new Date(c.createdAt).toISOString().slice(0, 10);
    const arr = groups.get(day) ?? [];
    arr.push(c);
    groups.set(day, arr);
  }
  const groupKeys = [...groups.keys()].sort().reverse();

  return (
    <div className="container max-w-4xl py-8 space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Recent changes</h1>
        <p className="text-sm text-muted-foreground">
          Frontmatter changes across all engineering docs.
        </p>
      </header>

      {groupKeys.length === 0 ? (
        <p className="text-sm text-muted-foreground">No changes yet.</p>
      ) : (
        groupKeys.map((day) => {
          const rows = groups.get(day)!;
          return (
            <Card key={day} className="p-4">
              <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground mb-3">
                {formatDay(day)}
              </h2>
              <ul className="space-y-1">
                {rows.map((c) => (
                  <li
                    key={c.id}
                    className="text-sm flex items-baseline gap-2"
                  >
                    <span className="text-xs text-muted-foreground w-12 shrink-0">
                      {formatTime(c.createdAt)}
                    </span>
                    {c.docType && (
                      <Badge variant="outline" className="text-xs shrink-0">
                        {c.docType}
                      </Badge>
                    )}
                    <Link
                      href={`/${c.docId}`}
                      className="font-medium hover:underline shrink-0"
                    >
                      {c.docTitle ?? c.docId}
                    </Link>
                    <span className="text-muted-foreground truncate">
                      {c.rendered}
                    </span>
                    {c.actor && (
                      <span className="text-xs text-muted-foreground shrink-0">
                        by {c.actor}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </Card>
          );
        })
      )}

      {!done && changes.length > 0 && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore ? "Loading…" : "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
}

function formatDay(day: string): string {
  const d = new Date(day);
  const now = new Date();
  const today = now.toISOString().slice(0, 10);
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  if (day === today) return "Today";
  if (day === yesterday) return "Yesterday";
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}
